import React, { useState } from 'react';
import { kioskAuthService } from '../services/api';
import { setKioskId } from '../utils/kioskId';

interface KioskRegistrationProps {
  onRegistrationComplete: () => void;
}

const KioskRegistration: React.FC<KioskRegistrationProps> = ({ onRegistrationComplete }) => {
  const [activationCode, setActivationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleActivate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!activationCode || activationCode.trim().length !== 6) {
      setError('Please enter a valid 6-character activation code');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      console.log('[Activation] Attempting to activate with code:', activationCode.toUpperCase());
      const result = await kioskAuthService.activate(activationCode.toUpperCase());
      console.log('[Activation] Success! Result:', result);

      // Store the UUID and info
      setKioskId(result.uuid, result.name, result.location);

      // Notify parent component
      onRegistrationComplete();
    } catch (err: any) {
      console.error('[Activation] Failed:', err);
      console.error('[Activation] Error response:', err.response);
      console.error('[Activation] Error data:', err.response?.data);
      console.error('[Activation] Error message:', err.message);
      setError(
        err.response?.data?.message ||
        err.message ||
        'Invalid activation code. Please check and try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
    setActivationCode(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🖥️</div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Kiosk Activation</h1>
          <p className="text-gray-600">Enter your activation code to get started</p>
        </div>

        <form onSubmit={handleActivate} className="space-y-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Activation Code
            </label>
            <input
              type="text"
              value={activationCode}
              onChange={handleInputChange}
              maxLength={6}
              className="w-full px-4 py-3 text-center text-2xl font-mono font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase tracking-widest"
              placeholder="ABC123"
              disabled={isLoading}
              autoFocus
            />
            <p className="mt-2 text-sm text-gray-500 text-center">
              Enter the 6-character code provided by your administrator
            </p>
          </div>

          <button
            type="submit"
            disabled={isLoading || activationCode.length !== 6}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Activating...
              </span>
            ) : (
              'Activate Kiosk'
            )}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-600 text-center">
            Don't have an activation code?
            <br />
            Contact your administrator to create a kiosk and get a code.
          </p>
        </div>
      </div>
    </div>
  );
};

export default KioskRegistration;
